/**
 * TallyItemsManagement.js
 * 
 * This component manages tally items and includes QR code scanning functionality.
 * It uses the QRScanner component from '../../components/QRScanner/QRScanner'
 * which internally uses the qr-scanner library for efficient QR code scanning.
 */
import { debounce } from 'lodash';
import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../components/AuthContext';
import Select from 'react-select';
import QRCode from 'react-qr-code';
import QRScanner from '../../components/QRScanner/QRScanner';
import QrScanner from 'qr-scanner';
import { 
  FaImage, 
  FaTimes, 
  FaEdit, 
  FaSearch, 
  FaSort,
  FaSortUp,
  FaSortDown,
  FaBox,
  FaTags,
  FaCamera,
  FaEye,
  FaQrcode,
  FaDownload,
  FaExclamationTriangle,
  FaWarehouse,
  FaChartBar,
  FaTh,
  FaTable
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';

export default function TallyItemsManagement() {
  const { user } = useContext(AuthContext);
  const [parameters, setParameters] = useState([]);
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  
  // Dark mode state
  const [darkMode, setDarkMode] = useState(false);
  
  const [itemFormData, setItemFormData] = useState({
    name: '',
    Name: '',
    parameter: '',
    brand: '',
    series: 'RS',
    images: [], // New images to upload
    imagePreviews: [], // Previews for new images
    existingImages: [] // Existing images from the item
  });
  const [itemEditId, setItemEditId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showStockSummaryModal, setShowStockSummaryModal] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrScannerLoading, setQrScannerLoading] = useState(false);
  const [qrCodeData, setQrCodeData] = useState('');
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [cameraFacingMode, setCameraFacingMode] = useState('environment');
  const [qrName , setQrName] = useState(null);

  // QR Scanner refs
  const qrScannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  // Add search state
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [stockGroupFilter, setStockGroupFilter] = useState('');
  const [stockGroups, setStockGroups] = useState([]);
  
  // Bulk assignment states
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssignData, setBulkAssignData] = useState({
    parameter: '',
    brand: ''
  });

  // Sync dark mode with localStorage
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = localStorage.getItem('darkMode') === 'true';
      setDarkMode(isDark);
    };
    
    checkDarkMode();
    const interval = setInterval(checkDarkMode, 500);
    
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all parameters without pagination for dropdown use
      const paramsRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/tally/parameters`, {
        params: {
          page: 1,
          limit: 1000, // Get all parameters for dropdown
          sortField: 'name',
          sortOrder: 'asc'
        }
      });
      setParameters(paramsRes.data.data || []);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Error fetching parameters',
        timer: 3000,
        showConfirmButton: false
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/companies/${user?.user?.company._id || user?.user?.company}`);
        setQrName(res.data.qrName);
      } catch (error) {
        console.error('Error fetching company:', error);
      }
    }
    fetchCompany();
  }, []);

  const fetchBrands = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/brands/all`, {});
      setBrands(res.data.data || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchStockGroups = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/tally/stock-groups/${user?.user?.company._id || user?.user?.company}`);
      setStockGroups(res.data.data || []);
    } catch (error) {
      console.error('Error fetching stock groups:', error);
    }
  };

  const fetchItems = useCallback(async (searchTerm = search) => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/tally/items`, {
        params: {
          page,
          limit,
          search: searchTerm,
          sortField,
          sortOrder,
          company: (user?.user?.company._id || user?.user?.company),
          requireParameter: false,
          brand: brandFilter,
          stockGroup: stockGroupFilter
        }
      });
      setItems(res.data.data);
      setTotal(res.data.total);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Error fetching items',
        timer: 3000,
        showConfirmButton: false
      });
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, sortField, sortOrder, user, brandFilter, stockGroupFilter]);

  // Add useMemo for debounced search
  const debouncedSearch = useMemo(() => {
    return debounce((value) => {
      setPage(1); // Only reset page when search actually changes
      fetchItems(value);
    }, 500);
  }, [fetchItems]);

  // Handle search change separately to avoid interfering with pagination
  const handleSearchChange = (value) => {
    setSearch(value);
    if (value !== search) {
      debouncedSearch(value);
    }
  };

  const handleBrandFilterChange = (selectedOption) => {
    setBrandFilter(selectedOption ? selectedOption.value : '');
    setPage(1); // Reset to first page when filter changes
  };

  const handleStockGroupFilterChange = (selectedOption) => {
    setStockGroupFilter(selectedOption ? selectedOption.value : '');
    setPage(1); // Reset to first page when filter changes
  };

  const handleItemUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('parameter', itemFormData.parameter);
      formDataToSend.append('brand', itemFormData.brand);
      formDataToSend.append('series', itemFormData.series);
      
      // Send existing images that should be kept
      if (itemFormData.existingImages && itemFormData.existingImages.length > 0) {
        formDataToSend.append('existingImages', JSON.stringify(itemFormData.existingImages));
      }
      
      // Append new images
      if (itemFormData.images && itemFormData.images.length > 0) {
        itemFormData.images.forEach((image) => {
          formDataToSend.append('images', image);
        });
      }

      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/tally/items/${itemEditId}`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Item updated successfully',
        timer: 2000,
        showConfirmButton: false
      });

      closeAllModals();
      fetchItems();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.response?.data?.error || 'Error updating item',
        timer: 3000,
        showConfirmButton: false
      });
    } finally {
      setLoading(false);
    }
  };

  const resetItemForm = () => {
    // Cleanup preview URLs to prevent memory leaks
    itemFormData.imagePreviews.forEach(url => URL.revokeObjectURL(url));
    
    setItemFormData({
      name: '',
      Name: '',
      parameter: '',
      brand: '',
      series: 'RS',
      images: [],
      imagePreviews: [],
      existingImages: []
    });
    setItemEditId(null);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newImages = [];
      const newPreviews = [];
      
      files.forEach(file => {
        newImages.push(file);
        newPreviews.push(URL.createObjectURL(file));
      });
      
      setItemFormData(prev => ({
        ...prev,
        images: [...prev.images, ...newImages],
        imagePreviews: [...prev.imagePreviews, ...newPreviews]
      }));
    }
  };

  const handleRemoveImage = (index) => {
    setItemFormData(prev => {
      const newImages = [...prev.images];
      const newPreviews = [...prev.imagePreviews];
      
      // Clean up blob URL to prevent memory leak
      if (newPreviews[index]) {
        URL.revokeObjectURL(newPreviews[index]);
      }
      
      newImages.splice(index, 1);
      newPreviews.splice(index, 1);
      
      return {
        ...prev,
        images: newImages,
        imagePreviews: newPreviews
      };
    });
  };

  const handleRemoveExistingImage = (index) => {
    setItemFormData(prev => {
      const newExistingImages = [...prev.existingImages];
      newExistingImages.splice(index, 1);
      
      return {
        ...prev,
        existingImages: newExistingImages
      };
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort className="text-gray-400" />;
    return sortOrder === 'asc' ? <FaSortUp className="text-blue-500" /> : <FaSortDown className="text-blue-500" />;
  };

  const handleViewItem = (item) => {
    setSelectedItem(item);
    setShowDetailsModal(true);
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setItemFormData({
      name: item.name || '',
      Name: item.Name || '',
      parameter: item.parameter?._id || '',
      brand: item.brand?._id || '',
      series: item.series || 'RS',
      images: [],
      imagePreviews: [], // For new images
      existingImages: item.images || [] // Store existing images separately
    });
    setItemEditId(item._id);
    setShowEditModal(true);
  };

  const handleShowQR = (item) => {
    setSelectedItem(item);
    setQrCodeData(item._id);
    setShowQRModal(true);
  };

  const handleShowStockSummary = (item) => {
    setSelectedItem(item);
    setShowStockSummaryModal(true);
  };

  const downloadQR = async (includeQrName = false) => {
    const svg = document.getElementById('qr-code-svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Set canvas size to SVG size
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      // Convert canvas to PNG data URL
      const pngDataUrl = canvas.toDataURL('image/png');
      
      if (includeQrName) {
        downloadQRWithName(pngDataUrl);
      } else {
        downloadQROnly(pngDataUrl);
      }
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const downloadQROnly = (qrImageData) => {
    // Create PDF with size 45mm x 65mm - QR only (portrait orientation)
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [45, 65]
    });
    // Calculate image size to fit PDF (35mm x 35mm)
    pdf.addImage(qrImageData, 'PNG', 5, 15, 35, 35);
    pdf.save(`${selectedItem.Name}_QR.pdf`);
  };

  const downloadQRWithName = (qrImageData) => {
    // Create PDF with size 45mm x 65mm - QR with name (portrait orientation)
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [45, 65]
    });
    
    // Add QR name text at the top (company's QR name)
    const qrNameText = qrName || 'DOMUS'; // Use company QR name or fallback to DOMUS
    pdf.setFontSize(12); // Increased font size for better visibility (was 10)
    pdf.setFont('helvetica', 'bold');
    
    // Calculate text position (centered at top with more spacing)
    const qrNameWidth = pdf.getTextWidth(qrNameText);
    const qrNameX = (45 - qrNameWidth) / 2; // Center horizontally in 45mm width
    const qrNameY = 8; // Increased top margin (was 6)
    
    pdf.text(qrNameText, qrNameX, qrNameY);
    
    // Add QR code below the QR name (centered)
    const qrSize = 25; // QR code size
    const qrX = (45 - qrSize) / 2; // Center horizontally in 45mm width
    const qrY = qrNameY + 4; // Increased spacing below QR name (was 3)
    pdf.addImage(qrImageData, 'PNG', qrX, qrY, qrSize, qrSize);
    
    // Add arrow pointing down to item name with padding
    const arrowY = qrY + qrSize + 4; // Increased padding above arrow (was 2)
    const arrowX = 45 / 2; // Center horizontally
    pdf.setLineWidth(0.5);
    pdf.line(arrowX, arrowY, arrowX, arrowY + 3); // Vertical line
    // Arrow head (small triangle)
    pdf.line(arrowX - 1, arrowY + 2, arrowX, arrowY + 3); // Left side of arrow head
    pdf.line(arrowX + 1, arrowY + 2, arrowX, arrowY + 3); // Right side of arrow head
    
    // Add item name below arrow (with line wrapping)
    const itemNameText = selectedItem.Name || 'Item';
    pdf.setFontSize(9); // Increased font size for better readability (was 8)
    pdf.setFont('helvetica', 'bold'); // Make item name bold
    
    // Split text into multiple lines if too long
    const maxWidth = 38; // Slightly reduced width for better margins
    const lines = pdf.splitTextToSize(itemNameText, maxWidth);
    
    // Calculate starting Y position for multiple lines (center them)
    const lineHeight = 3.0; // Increased line spacing for better readability (was 2.5)
    const totalTextHeight = lines.length * lineHeight;
    const startY = arrowY + 8; // Increased padding below arrow for minor spacing (was 7)
    
    // Draw each line centered with improved styling
    lines.forEach((line, index) => {
      const lineWidth = pdf.getTextWidth(line);
      const lineX = (45 - lineWidth) / 2; // Center horizontally
      const lineY = startY + (index * lineHeight);
      pdf.text(line, lineX, lineY);
    });
    
    pdf.save(`${selectedItem.Name}_QR_with_name.pdf`);
  };

  const getAvailableCameras = async () => {
    try {
      const cameras = await QrScanner.listCameras(true);
      console.log('Available cameras:', cameras);
      setAvailableCameras(cameras);
      
      // If no camera is selected, default to the first one or prefer rear camera
      if (!selectedCameraId && cameras.length > 0) {
        const rearCamera = cameras.find(camera => 
          camera.label.toLowerCase().includes('back') || 
          camera.label.toLowerCase().includes('rear') ||
          camera.label.toLowerCase().includes('environment')
        );
        setSelectedCameraId(rearCamera ? rearCamera.id : cameras[0].id);
      }
    } catch (error) {
      console.error('Error getting cameras:', error);
      Swal.fire({
        icon: 'error',
        title: 'Camera Error',
        text: 'Could not access camera devices. Please ensure camera permissions are granted.',
      });
    }
  };

  const handleExportItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/tally/items/export`, {
        params: {
          company: (user?.user?.company._id || user?.user?.company),
          brand: brandFilter
        },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tally-items-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      Swal.fire({
        icon: 'success',
        title: 'Export Successful!',
        text: 'Items have been exported to Excel',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Export Failed!',
        text: 'Error exporting items to Excel',
        timer: 3000,
        showConfirmButton: false
      });
    } finally {
      setLoading(false);
    }
  };

  // Bulk assignment functions
  const handleItemSelect = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === items.length && items.length > 0) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map(item => item._id)));
    }
  };

  const handleBulkAssign = async () => {
    if (selectedItems.size === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Items Selected',
        text: 'Please select items to assign parameters/brands',
        timer: 3000,
        showConfirmButton: false
      });
      return;
    }

    if (!bulkAssignData.parameter && !bulkAssignData.brand) {
      Swal.fire({
        icon: 'warning',
        title: 'No Assignment Data',
        text: 'Please select a parameter or brand to assign',
        timer: 3000,
        showConfirmButton: false
      });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/tally/items/bulk-assign`, {
        itemIds: Array.from(selectedItems),
        parameter: bulkAssignData.parameter || null,
        brand: bulkAssignData.brand || null,
        updatedBy: user?.user?._id
      });

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Bulk Assignment Successful!',
          text: `Successfully updated ${response.data.data.modifiedCount} items`,
          timer: 2000,
          showConfirmButton: false
        });

        // Reset selections and refresh items
        setSelectedItems(new Set());
        setBulkAssignData({ parameter: '', brand: '' });
        setShowBulkAssignModal(false);
        fetchItems();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Bulk Assignment Failed!',
        text: 'Error updating items',
        timer: 3000,
        showConfirmButton: false
      });
    } finally {
      setLoading(false);
    }
  };

  const openQRScanner = async () => {
    await getAvailableCameras();
    setShowQRScanner(true);
    // Start scanner after modal is shown
    setTimeout(() => startQRScanner(), 100);
  };

  const startQRScanner = async () => {
    try {
      setQrScannerLoading(true);

      // Get available cameras first if not already loaded
      if (availableCameras.length === 0) {
        await getAvailableCameras();
      }

      // Since we're now using the QRScanner component, 
      // we don't need to manage the scanner instance directly here.
      // The QRScanner component handles the scanner initialization.
      setQrScannerLoading(false);
      console.log('QR Scanner ready to start');

    } catch (error) {
      console.error('Error preparing QR scanner:', error);
      setQrScannerLoading(false);
      
      let errorMessage = 'Could not start QR scanner.';
      if (error.message?.includes('Permission denied') || error.name === 'NotAllowedError') {
        errorMessage = 'Camera access was denied. Please allow camera permissions and try again.';
      } else if (error.message?.includes('NotFoundError') || error.name === 'NotFoundError') {
        errorMessage = 'No cameras found on this device.';
      } else if (error.message?.includes('OverconstrainedError') || error.name === 'OverconstrainedError') {
        errorMessage = 'The selected camera is not available. Please try a different camera.';
        // Reset to default camera selection
        if (availableCameras.length > 0) {
          setSelectedCameraId(availableCameras[0].id);
        }
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Camera Error',
        text: errorMessage,
      });
    }
  };

  const stopQRScanner = async () => {
    // The QRScanner component handles cleanup internally
    // when it unmounts. We just need to update our state.
    setShowQRScanner(false);
    setQrScannerLoading(false);
  };

  const handleQRScanSuccess = async (qrCodeData) => {
    if (qrCodeData) {
      try {
        // Stop scanning first
        stopQRScanner();
        
        // Assume the QR code contains the item ObjectId
        const itemId = qrCodeData.trim();
        console.log('QR Code scanned:', itemId);
        
        // Basic validation for MongoDB ObjectId format
        if (!/^[0-9a-fA-F]{24}$/.test(itemId)) {
          console.warn('QR code does not contain a valid ObjectId:', itemId);
          throw new Error('Invalid QR code format. Expected a valid item ID.');
        }
        
        // Fetch item details by ID
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/tally/items/${itemId}`);
        
        if (response.data.success) {
          const scannedItem = response.data.data;
          console.log('Scanned item data:', scannedItem);
          
          // Check if the scanned item belongs to the user's company
          const userCompanyId = user?.user?.company?._id || user?.user?.company;
          const itemCompanyId = scannedItem.companyID?.toString();
          
          console.log('Company validation:', {
            userCompanyId,
            itemCompanyId,
            match: userCompanyId?.toString() === itemCompanyId
          });
          
          if (userCompanyId && itemCompanyId && userCompanyId.toString() !== itemCompanyId) {
            console.warn('Item does not belong to user company:', {
              itemName: scannedItem.Name,
              itemCompany: itemCompanyId,
              userCompany: userCompanyId
            });
            
            Swal.fire({
              icon: 'error',
              title: 'Item Not Found',
              text: 'Item not found',
              timer: 2000,
              showConfirmButton: false
            });
            return;
          }
          
          setSelectedItem(scannedItem);
          setShowDetailsModal(true);
          
          Swal.fire({
            icon: 'success',
            title: 'QR Code Scanned!',
            text: `Item "${scannedItem.Name}" found successfully`,
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          console.error('API response not successful:', response.data);
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: 'Item not found',
            timer: 2000,
            showConfirmButton: false
          });
        }
      } catch (error) {
        console.error('Error finding scanned item:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        
        let errorMessage = 'Item not found';
        if (error.response?.status === 404) {
          errorMessage = 'Item not found';
        } else if (error.response?.status === 401) {
          errorMessage = 'Authentication failed. Please login again.';
        } else if (error.response?.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (error.message?.includes('Invalid QR code format')) {
          errorMessage = 'Invalid QR code format';
        }
        
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: errorMessage,
          timer: 2000,
          showConfirmButton: false
        });
      }
    }
  };

  const handleQRError = (error) => {
    console.error('QR Scanner error:', error);
    setQrScannerLoading(false);
    Swal.fire({
      icon: 'error',
      title: 'Camera Error',
      text: typeof error === 'string' ? error : 'Failed to access camera. Please check your permissions.',
    });
  };

  const handleQRScan = (data) => {
    if (data) {
      console.log('QR Code detected:', data);
      handleQRScanSuccess(data);
    }
  };

  const handleCameraLoad = (isLoading) => {
    setQrScannerLoading(isLoading);
  };

  const closeAllModals = () => {
    setShowDetailsModal(false);
    setShowEditModal(false);
    setShowQRModal(false);
    setShowStockSummaryModal(false);
    setShowImageModal(false);
    setSelectedItem(null);
    setItemEditId(null);
    stopQRScanner(); // Ensure QR scanner is stopped
    resetItemForm();
  };

  // Fetch initial data
  useEffect(() => {
    fetchData();
    fetchBrands();
    fetchStockGroups();
  }, []);

  useEffect(() => {
    fetchItems();
  }, [page, limit, sortField, sortOrder, fetchItems]);

  // Update useEffect for search - separate from pagination
  useEffect(() => {
    // Don't use debouncedSearch here as it interferes with pagination
    // The debounced search is handled in handleSearchChange
  }, []);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        try {
          html5QrCodeRef.current.stop();
          if (typeof html5QrCodeRef.current.clear === 'function') {
            html5QrCodeRef.current.clear();
          } else if (typeof html5QrCodeRef.current.destroy === 'function') {
            html5QrCodeRef.current.destroy();
          }
        } catch (error) {
          console.error('Error cleaning up QR scanner:', error);
        }
      }
      stopQRScanner();
    };
  }, []);

  const parameterOptions = parameters.map(param => ({
    value: param._id,
    label: `${param.name} (${param.category?.name})`
  }));

  const brandOptions = brands.map(brand => ({
    value: brand._id,
    label: brand.name
  }));

  // Series options
  const seriesOptions = [
    { value: 'RS', label: 'Regular Stock (RS)' },
    { value: 'AO', label: 'Against Order (AO)' },
    { value: 'SC', label: 'Stock Clearance (SC)' },
    { value: 'DC', label: 'Discontinued (DC)' },
    { value: 'ITF', label: 'Item to Focus (ITF)' },
    { value: 'OT', label: 'One Time (OT)' }
  ];

  // Filter options for react-select
  const brandFilterOptions = [
    { value: '', label: 'All Brands' },
    { value: 'unassigned', label: 'Unassigned' },
    ...brands.map(brand => ({
      value: brand._id,
      label: brand.name
    }))
  ];

  const stockGroupFilterOptions = [
    { value: '', label: 'All Stock Groups' },
    { value: 'unassigned', label: 'Unassigned' },
    ...stockGroups.map(group => ({
      value: group._id,
      label: group.Name
    }))
  ];

  // Bulk assignment options for react-select
  const bulkParameterOptions = [
    { value: '', label: "Don't change parameter" },
    { value: 'unassign', label: 'Remove parameter assignment' },
    ...parameters.map(param => ({
      value: param._id,
      label: `${param.name} (${param.category?.name})`
    }))
  ];

  const bulkBrandOptions = [
    { value: '', label: "Don't change brand" },
    { value: 'unassign', label: 'Remove brand assignment' },
    ...brands.map(brand => ({
      value: brand._id,
      label: brand.name
    }))
  ];

  // Use server-side sorted items directly
  const sortedItems = items;

  // Custom Select styles for dark mode
  const selectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: darkMode ? 'rgb(15 23 42)' : 'white',
      borderColor: darkMode ? 'rgb(51 65 85)' : 'rgb(216 180 254)',
      borderWidth: '2px',
      borderRadius: '1rem',
      padding: '0.5rem',
      boxShadow: 'none',
      '&:hover': {
        borderColor: darkMode ? 'rgb(59 130 246)' : 'rgb(168 85 247)'
      }
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: darkMode ? 'rgb(30 41 59)' : 'white',
      border: darkMode ? '2px solid rgb(51 65 85)' : '2px solid rgb(216 180 254)',
      borderRadius: '1rem',
      overflow: 'hidden'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused 
        ? (darkMode ? 'rgb(51 65 85)' : 'rgb(243 232 255)')
        : (darkMode ? 'rgb(30 41 59)' : 'white'),
      color: darkMode ? 'rgb(229 231 235)' : 'rgb(17 24 39)',
      fontWeight: '600',
      '&:hover': {
        backgroundColor: darkMode ? 'rgb(51 65 85)' : 'rgb(243 232 255)'
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: darkMode ? 'rgb(229 231 235)' : 'rgb(17 24 39)',
      fontWeight: '600'
    }),
    placeholder: (base) => ({
      ...base,
      color: darkMode ? 'rgb(100 116 139)' : 'rgb(192 132 252)'
    }),
    input: (base) => ({
      ...base,
      color: darkMode ? 'white' : 'rgb(17 24 39)'
    })
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 5px rgba(99, 102, 241, 0.5); }
          50% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.8); }
        }
        .card-hover {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .card-hover:hover {
          transform: translateY(-8px) scale(1.02);
        }
        .animated-gradient {
          background: linear-gradient(-45deg, #667eea, #764ba2, #f093fb, #4facfe);
          background-size: 400% 400%;
          animation: gradientShift 15s ease infinite;
        }
      `}</style>
      
      <div className={`min-h-screen transition-all duration-500 ${
        darkMode 
          ? 'bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950' 
          : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'
      }`}>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-10">
          
          {/* Premium Header Card */}
          <div className={`relative overflow-hidden border rounded-3xl shadow-2xl px-8 py-8 mb-10 transition-all duration-500 group ${
            darkMode 
              ? 'bg-gradient-to-r from-slate-900/95 via-blue-900/90 to-purple-900/95 border-blue-700/30' 
              : 'bg-white/80 backdrop-blur-xl border-white/60 shadow-purple-200/50'
          }`}>
            {/* Animated background gradient */}
            <div className={`absolute inset-0 opacity-20 transition-opacity duration-500 group-hover:opacity-30 ${
              darkMode ? 'animated-gradient' : 'bg-gradient-to-r from-purple-200 via-pink-200 to-blue-200'
            }`}></div>            
            <div className="relative flex flex-col gap-6">
              {/* Mobile-first header layout */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
                <div className="flex items-center gap-3 sm:gap-5">
                  <div className={`p-3 sm:p-4 rounded-2xl shadow-lg transition-all duration-300 group-hover:scale-110 ${
                    darkMode 
                      ? 'bg-gradient-to-br from-blue-600 to-purple-600 shadow-blue-500/50' 
                      : 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-purple-500/30'
                  }`} style={{ animation: 'float 3s ease-in-out infinite' }}>
                    <FaBox className="text-2xl sm:text-3xl text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h1 className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold tracking-tight ${
                      darkMode 
                        ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400' 
                        : 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600'
                    }`}>
                      Tally Items
                    </h1>
                    <p className={`text-xs sm:text-sm mt-1 sm:mt-2 font-medium ${
                      darkMode ? 'text-blue-300/90' : 'text-purple-700/80'
                    }`}>
                      ✨ Manage tally items • {total} items total
                    </p>
                  </div>
                </div>
                
                {/* Mobile-optimized export button */}
                <button
                  onClick={handleExportItems}
                  disabled={loading}
                  className={`flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 lg:px-8 py-3 sm:py-4 font-bold rounded-xl sm:rounded-2xl transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ${
                    darkMode
                      ? 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white shadow-emerald-500/50'
                      : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-green-500/30'
                  }`}
                >
                  <FaDownload className="text-lg sm:text-xl" />
                  <span className="hidden sm:inline">Export to Excel</span>
                  <span className="sm:hidden">Export</span>
                </button>
              </div>

              {/* QR Name display - full width on mobile */}
              {qrName && (
                <div className={`p-3 rounded-xl border-2 shadow-md ${
                  darkMode 
                    ? 'bg-blue-900/30 border-blue-600/50 backdrop-blur-sm' 
                    : 'bg-blue-50 border-blue-300'
                }`}>
                  <div className="flex items-center gap-2">
                    <FaQrcode className={darkMode ? 'text-blue-400' : 'text-blue-600'} />
                    <span className={`text-sm font-bold ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                      QR Name: {qrName}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div> {/* Close Premium Header Card */}

        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6" style={{ display: 'none' }}>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                <FaBox className="text-indigo-600" />
                Tally Items Management
              </h1>
              <p className="text-gray-600 mt-2">Manage and assign parameters to tally items</p>
              {qrName && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FaQrcode className="text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">QR Name:</span>
                    <span className="text-sm font-bold text-blue-900">{qrName}</span>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">This name will appear on QR code labels</p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm text-gray-500">Total Items</div>
                <div className="text-2xl font-bold text-indigo-600">{total}</div>
              </div>
              <button
                onClick={handleExportItems}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                <FaDownload />
                Export to Excel
              </button>
            </div>
          </div>
        </div>

        {/* Premium Items Section - Full Width */}
        <div className={`rounded-3xl shadow-2xl border-2 overflow-hidden transition-all duration-500 ${
          darkMode
            ? 'bg-gradient-to-br from-slate-900/95 to-slate-800/90 border-slate-700/50 shadow-blue-500/10'
            : 'bg-white border-purple-200/60 shadow-purple-500/20'
        }`}>
          {/* Premium Search Header */}
          <div className={`p-8 border-b-2 ${
            darkMode ? 'border-slate-700/50 bg-slate-800/50' : 'border-purple-100 bg-gradient-to-r from-purple-50/50 to-pink-50/50'
          }`}>
            <div className="flex flex-col gap-4 mb-6">
              {/* Header row - responsive layout */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h2 className={`text-xl sm:text-2xl font-black tracking-tight ${
                    darkMode
                      ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400'
                      : 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600'
                  }`}>
                    Items List ({sortedItems.length} of {total})
                  </h2>
                  {/* Filter badges - mobile responsive */}
                  <div className={`text-sm font-semibold mt-2 flex flex-wrap gap-2 ${
                    darkMode ? 'text-slate-400' : 'text-gray-600'
                  }`}>
                    {brandFilter && (
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'
                      }`}>
                        🏷️ Brand: {brandFilter === 'unassigned' ? 'Unassigned' : brands.find(b => b._id === brandFilter)?.name || 'Unknown'}
                      </span>
                    )}
                    {stockGroupFilter && (
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                      }`}>
                        📊 Stock Group: {stockGroupFilter === 'unassigned' ? 'Unassigned' : stockGroups.find(g => g._id === stockGroupFilter)?.Name || 'Unknown'}
                      </span>
                    )}
                  </div>
                </div>
                {/* QR Scanner Button - responsive */}
                <button
                  onClick={openQRScanner}
                  className={`flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 rounded-xl font-black shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 whitespace-nowrap ${
                    darkMode
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white shadow-purple-500/50'
                      : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white shadow-purple-500/30'
                  }`}
                >
                  <FaQrcode className="text-lg" />
                  <span className="hidden sm:inline">Scan QR Code</span>
                  <span className="sm:hidden">Scan</span>
                </button>
              </div>
            </div>
            
            {/* Premium Search and Filters - Mobile-first responsive */}
            <div className="flex flex-col gap-4">
              {/* Search bar - always full width */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className={`h-4 w-4 sm:h-5 sm:w-5 ${
                    darkMode ? 'text-blue-400' : 'text-purple-500'
                  }`} style={{ animation: 'pulse 2s ease-in-out infinite' }} />
                </div>
                <input
                  type="text"
                  placeholder="Search items by name, category, or HSN code..."
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className={`w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 text-sm sm:text-base rounded-xl border-2 font-medium transition-all focus:outline-none focus:ring-4 shadow-md ${
                    darkMode
                      ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-500 focus:ring-blue-500/30 focus:border-blue-500 hover:border-blue-600'
                      : 'bg-white border-purple-300 text-gray-900 placeholder-gray-400 focus:ring-purple-500/30 focus:border-purple-500 hover:border-purple-400'
                  }`}
                />
              </div>
              
              {/* Filter dropdowns and View Mode Toggle - inline layout */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <Select
                  value={brandFilterOptions.find(option => option.value === brandFilter)}
                  onChange={handleBrandFilterChange}
                  options={brandFilterOptions}
                  placeholder="🏷️ Filter by Brand"
                  isSearchable
                  isClearable
                  classNamePrefix="react-select"
                  styles={{
                    ...selectStyles,
                    control: (base, state) => ({
                      ...selectStyles.control(base, state),
                      minHeight: '44px',
                      fontSize: '14px'
                    })
                  }}
                />
                <Select
                  value={stockGroupFilterOptions.find(option => option.value === stockGroupFilter)}
                  onChange={handleStockGroupFilterChange}
                  options={stockGroupFilterOptions}
                  placeholder="📊 Filter by Stock Group"
                  isSearchable
                  isClearable
                  classNamePrefix="react-select"
                  styles={{
                    ...selectStyles,
                    control: (base, state) => ({
                      ...selectStyles.control(base, state),
                      minHeight: '44px',
                      fontSize: '14px'
                    })
                  }}
                />
                
                {/* View Mode Toggle - Inline with filters */}
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-semibold whitespace-nowrap ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>View:</span>
                  <div className={`flex rounded-lg border-2 overflow-hidden shadow-sm flex-1 ${
                    darkMode ? 'border-gray-600' : 'border-purple-300'
                  }`}>
                    <button
                      onClick={() => setViewMode('cards')}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        viewMode === 'cards' 
                          ? 'bg-blue-600 text-white shadow-inner' 
                          : darkMode 
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <FaTh className="w-4 h-4" />
                      <span className="hidden sm:inline">Cards</span>
                    </button>
                    <button
                      onClick={() => setViewMode('table')}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        viewMode === 'table' 
                          ? 'bg-blue-600 text-white shadow-inner' 
                          : darkMode 
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <FaTable className="w-4 h-4" />
                      <span className="hidden sm:inline">Table</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Premium Bulk Assignment Controls - Mobile responsive */}
            {selectedItems.size > 0 && (
              <div className={`rounded-2xl border-2 p-4 sm:p-6 mt-6 shadow-lg ${
                darkMode
                  ? 'bg-blue-900/30 border-blue-700/50'
                  : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200'
              }`} style={{ animation: 'slideUp 0.3s ease-out' }}>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                    <span className={`text-base sm:text-lg font-black ${
                      darkMode ? 'text-blue-300' : 'text-blue-700'
                    }`}>
                      ✓ {selectedItems.size} item(s) selected
                    </span>
                    <button
                      onClick={() => setSelectedItems(new Set())}
                      className={`text-sm font-bold underline transition-colors self-start ${
                        darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                      }`}
                    >
                      Clear selection
                    </button>
                  </div>
                  <button
                    onClick={() => setShowBulkAssignModal(true)}
                    className={`px-4 sm:px-6 py-3 rounded-xl font-black shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 whitespace-nowrap ${
                      darkMode
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white shadow-blue-500/50'
                        : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-400 hover:to-purple-400 text-white shadow-blue-500/30'
                    }`}
                  >
                    🎯 Bulk Assign Parameters/Brands
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Premium Card Grid */}
          <div className="p-6">
            {/* Loading State */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className={`animate-spin rounded-full h-16 w-16 border-b-4 ${
                  darkMode ? 'border-blue-500' : 'border-indigo-600'
                }`}></div>
                <span className={`mt-4 text-lg font-medium ${
                  darkMode ? 'text-blue-300' : 'text-gray-600'
                }`}>Loading items...</span>
              </div>
            ) : sortedItems.length === 0 ? (
              /* Empty State */
              <div className="flex flex-col items-center justify-center py-20">
                <FaBox className={`text-6xl mb-4 ${darkMode ? 'text-slate-600' : 'text-gray-300'}`} />
                <p className={`text-xl font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  No items found
                </p>
                <p className={`text-sm mt-2 ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                  Try adjusting your search or filters
                </p>
              </div>
            ) : viewMode === 'cards' ? (
              /* Card Grid - Mobile responsive */
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                {sortedItems.map((item, index) => (
                  <div
                    key={item._id}
                    className={`card-hover rounded-xl sm:rounded-2xl border-2 p-4 sm:p-6 transition-all duration-500 shadow-lg ${
                      darkMode
                        ? 'bg-gradient-to-br from-slate-800/80 to-slate-900/90 border-slate-700/50 hover:border-blue-500/50 hover:shadow-blue-500/20 hover:shadow-xl'
                        : 'bg-gradient-to-br from-white to-purple-50/30 border-purple-200/60 hover:border-purple-400/50 hover:shadow-purple-500/20 hover:shadow-xl'
                    }`}
                    style={{
                      animation: `slideUp 0.5s ease-out ${index * 0.05}s backwards`
                    }}
                  >
                    {/* Card Header - Mobile responsive */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-5">
                      <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item._id)}
                          onChange={() => handleItemSelect(item._id)}
                          className={`rounded border-2 w-4 h-4 sm:w-5 sm:h-5 transition-all ${
                            darkMode
                              ? 'border-slate-600 text-blue-600 focus:ring-blue-500/50 bg-slate-700'
                              : 'border-purple-300 text-indigo-600 focus:ring-indigo-500/30'
                          }`}
                        />
                        
                        {/* ID Badge */}
                        <span className={`px-2 sm:px-4 py-1 sm:py-1.5 text-xs font-black rounded-lg sm:rounded-xl shadow-md ${
                          darkMode
                            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-blue-500/30'
                            : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-blue-500/20'
                        }`}>
                          {item.id || 'N/A'}
                        </span>
                        
                        {/* Series Badge */}
                        <span className={`px-2 sm:px-4 py-1 sm:py-1.5 text-xs font-black rounded-full shadow-md ${
                          item.series === 'RS'
                            ? (darkMode ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-green-500/30' : 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-green-500/20')
                            : (darkMode ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-purple-500/30' : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-purple-500/20')
                        }`}>
                          {item.series || 'RS'}
                        </span>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-1 sm:gap-2 self-end sm:self-auto">
                        <button
                          onClick={() => handleViewItem(item)}
                          className={`p-2 sm:p-2.5 rounded-lg sm:rounded-xl transition-all duration-300 transform hover:scale-125 active:scale-95 shadow-md ${
                            darkMode
                              ? 'text-blue-400 hover:bg-blue-900/50 hover:shadow-blue-500/50'
                              : 'text-blue-600 hover:bg-blue-100 hover:shadow-blue-500/30'
                          }`}
                          title="View details"
                        >
                          <FaEye className="text-sm sm:text-lg" />
                        </button>
                        <button
                          onClick={() => handleEditItem(item)}
                          className={`p-2.5 rounded-xl transition-all duration-300 transform hover:scale-125 active:scale-95 shadow-md ${
                            darkMode
                              ? 'text-emerald-400 hover:bg-emerald-900/50 hover:shadow-emerald-500/50'
                              : 'text-indigo-600 hover:bg-indigo-100 hover:shadow-indigo-500/30'
                          }`}
                          title="Edit item"
                        >
                          <FaEdit className="text-lg" />
                        </button>
                        <button
                          onClick={() => handleShowQR(item)}
                          className={`p-2.5 rounded-xl transition-all duration-300 transform hover:scale-125 active:scale-95 shadow-md ${
                            darkMode
                              ? 'text-purple-400 hover:bg-purple-900/50 hover:shadow-purple-500/50'
                              : 'text-purple-600 hover:bg-purple-100 hover:shadow-purple-500/30'
                          }`}
                          title="Show QR Code"
                        >
                          <FaQrcode className="text-lg" />
                        </button>
                      </div>
                    </div>

                    {/* Item Name */}
                    <h3 className={`text-xl font-black mb-3 tracking-tight ${
                      darkMode 
                        ? 'text-white' 
                        : 'text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-gray-900'
                    }`}>
                      {item.Name}
                    </h3>
                    
                    {/* Category Badge */}
                    <div className="mb-4">
                      <span className={`inline-flex items-center px-3 py-1 text-xs font-bold rounded-full shadow-sm ${
                        darkMode
                          ? 'bg-slate-700 text-slate-300 shadow-slate-900/50'
                          : 'bg-gray-200 text-gray-700 shadow-gray-400/20'
                      }`}>
                        📂 {item.category}
                      </span>
                    </div>

                    {/* Details Grid */}
                    <div className={`grid grid-cols-2 gap-4 p-5 rounded-2xl border mb-5 ${
                      darkMode 
                        ? 'bg-slate-950/50 border-slate-700/50' 
                        : 'bg-gradient-to-br from-purple-50/50 to-pink-50/50 border-purple-200/50'
                    }`}>
                      <div className="space-y-1.5">
                        <p className={`text-xs font-black uppercase tracking-wider ${
                          darkMode ? 'text-blue-400' : 'text-purple-600'
                        }`}>
                          📦 Parameter
                        </p>
                        {item.parameter ? (
                          <p className={`text-sm font-extrabold ${
                            darkMode ? 'text-emerald-400' : 'text-green-700'
                          }`}>
                            {item.parameter.name}
                          </p>
                        ) : (
                          <p className={`text-sm font-medium ${
                            darkMode ? 'text-slate-500' : 'text-gray-400'
                          }`}>
                            Unassigned
                          </p>
                        )}
                      </div>
                      
                      <div className="space-y-1.5">
                        <p className={`text-xs font-black uppercase tracking-wider ${
                          darkMode ? 'text-purple-400' : 'text-pink-600'
                        }`}>
                          🏷️ Brand
                        </p>
                        {item.brand ? (
                          <p className={`text-sm font-extrabold ${
                            darkMode ? 'text-blue-400' : 'text-blue-700'
                          }`}>
                            {item.brand.name}
                          </p>
                        ) : (
                          <p className={`text-sm font-medium ${
                            darkMode ? 'text-slate-500' : 'text-gray-400'
                          }`}>
                            Unassigned
                          </p>
                        )}
                      </div>
                      
                      <div className="space-y-1.5">
                        <p className={`text-xs font-black uppercase tracking-wider ${
                          darkMode ? 'text-orange-400' : 'text-orange-600'
                        }`}>
                          📊 Stock Group
                        </p>
                        {item.parentId ? (
                          <p className={`text-sm font-extrabold ${
                            darkMode ? 'text-orange-400' : 'text-orange-700'
                          }`}>
                            {item.parentId.Name}
                          </p>
                        ) : (
                          <p className={`text-sm font-medium ${
                            darkMode ? 'text-slate-500' : 'text-gray-400'
                          }`}>
                            Unassigned
                          </p>
                        )}
                      </div>
                      
                      <div className="space-y-1.5">
                        <p className={`text-xs font-black uppercase tracking-wider ${
                          darkMode ? 'text-pink-400' : 'text-pink-600'
                        }`}>
                          🔢 HSN Code
                        </p>
                        <p className={`text-sm font-extrabold ${
                          darkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {item.hsnCode || 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* Images Section */}
                    {(item.imageUrl || (item.imageIds && item.imageIds.length > 0)) && (
                      <div className="mt-4">
                        {/* Image Preview */}
                        <div className="mb-3 flex justify-center">
                          <img
                            src={(() => {
                              // Handle both populated (object) and non-populated (string) imageIds
                              const firstImageId = item.imageIds[0];
                              const imageId = typeof firstImageId === 'object' ? firstImageId._id : firstImageId;
                              const imageUrl = item.imageUrl || `${process.env.REACT_APP_API_BASE_URL}/tally/images/${imageId}`;
                              console.log('🖼️ Image URL for', item.Name, ':', imageUrl);
                              console.log('🔍 ImageIds structure:', item.imageIds);
                              console.log('🔍 First image ID:', firstImageId, 'extracted ID:', imageId);
                              return imageUrl;
                            })()}
                            alt={item.Name}
                            className={`w-20 h-20 object-cover rounded-lg border-2 cursor-pointer transition-all hover:scale-110 shadow-md ${
                              darkMode
                                ? 'border-slate-600 hover:border-blue-500'
                                : 'border-gray-300 hover:border-purple-400'
                            }`}
                            onClick={() => {
                              const firstImageId = item.imageIds[0];
                              const imageId = typeof firstImageId === 'object' ? firstImageId._id : firstImageId;
                              setShowImageModal(item.imageUrl || `${process.env.REACT_APP_API_BASE_URL}/tally/images/${imageId}`);
                            }}
                            onError={(e) => {
                              console.error('❌ Image failed to load:', e.target.src);
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          {/* Error placeholder */}
                          <div className={`w-20 h-20 rounded-lg border-2 border-dashed hidden items-center justify-center ${
                            darkMode 
                              ? 'border-slate-600 bg-slate-800 text-slate-500' 
                              : 'border-gray-300 bg-gray-100 text-gray-400'
                          }`}>
                            <FaImage className="text-2xl" />
                          </div>
                        </div>
                        
                        <button
                          onClick={() => {
                            const firstImageId = item.imageIds[0];
                            const imageId = typeof firstImageId === 'object' ? firstImageId._id : firstImageId;
                            setShowImageModal(item.imageUrl || `${process.env.REACT_APP_API_BASE_URL}/tally/images/${imageId}`);
                          }}
                          className={`w-full p-3 rounded-xl border-2 border-dashed transition-all hover:scale-105 flex items-center justify-center gap-2 ${
                            darkMode
                              ? 'border-blue-500/50 bg-blue-900/20 hover:bg-blue-900/40 text-blue-400'
                              : 'border-purple-300 bg-purple-50 hover:bg-purple-100 text-purple-600'
                          }`}
                        >
                          <FaImage className="text-lg" />
                          <span className="font-bold">
                            View Image{item.imageIds && item.imageIds.length > 1 ? `s (${item.imageIds.length})` : ''}
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              /* Table View */
              <div className={`rounded-xl shadow-lg border-2 overflow-hidden ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className={`${
                      darkMode ? 'bg-gradient-to-r from-gray-700 to-gray-800' : 'bg-gradient-to-r from-blue-50 to-indigo-50'
                    }`}>
                      <tr>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          <input
                            type="checkbox"
                            checked={sortedItems.length > 0 && selectedItems.size === sortedItems.length}
                            onChange={handleSelectAll}
                            className="rounded border-2 w-4 h-4"
                          />
                        </th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>ID / Series</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>Item Details</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>Parameter</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>Brand</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>Stock Group</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>Net Available</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>Image</th>
                        <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${
                      darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'
                    }`}>
                      {sortedItems.map((item) => (
                        <tr key={item._id} className={`${
                          darkMode ? 'hover:bg-gray-700' : 'hover:bg-blue-50'
                        } transition-colors duration-150`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(item._id)}
                              onChange={() => handleItemSelect(item._id)}
                              className={`rounded border-2 w-4 h-4 transition-all ${
                                darkMode
                                  ? 'border-slate-600 text-blue-600 focus:ring-blue-500/50 bg-slate-700'
                                  : 'border-purple-300 text-indigo-600 focus:ring-indigo-500/30'
                              }`}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              <span className={`px-3 py-1 text-xs font-bold rounded-lg shadow-sm inline-block w-fit ${
                                darkMode
                                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white'
                                  : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                              }`}>
                                {item.id || 'N/A'}
                              </span>
                              <span className={`px-3 py-1 text-xs font-bold rounded-full shadow-sm inline-block w-fit ${
                                item.series === 'RS'
                                  ? (darkMode ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white' : 'bg-gradient-to-r from-emerald-500 to-green-500 text-white')
                                  : (darkMode ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white')
                              }`}>
                                {item.series || 'RS'}
                              </span>
                            </div>
                          </td>
                          <td className={`px-6 py-4 ${
                            darkMode ? 'text-gray-200' : 'text-gray-900'
                          }`}>
                            <div className="max-w-xs">
                              <div className="font-bold text-sm mb-1">{item.Name}</div>
                              <div className={`text-xs ${
                                darkMode ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                <span className="font-medium">Category:</span> {item.category}
                              </div>
                              {item.hsnCode && (
                                <div className={`text-xs mt-1 ${
                                  darkMode ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  <span className="font-medium">HSN:</span> {item.hsnCode}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                            darkMode ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            {item.parameter ? (
                              <span className={`font-bold ${
                                darkMode ? 'text-emerald-400' : 'text-green-700'
                              }`}>
                                {item.parameter.name}
                              </span>
                            ) : (
                              <span className={`text-xs font-medium italic ${
                                darkMode ? 'text-slate-500' : 'text-gray-400'
                              }`}>
                                Unassigned
                              </span>
                            )}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                            darkMode ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            {item.brand ? (
                              <span className={`font-bold ${
                                darkMode ? 'text-blue-400' : 'text-blue-700'
                              }`}>
                                {item.brand.name}
                              </span>
                            ) : (
                              <span className={`text-xs font-medium italic ${
                                darkMode ? 'text-slate-500' : 'text-gray-400'
                              }`}>
                                Unassigned
                              </span>
                            )}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                            darkMode ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            {item.parentId ? (
                              <span className={`font-bold ${
                                darkMode ? 'text-orange-400' : 'text-orange-700'
                              }`}>
                                {item.parentId.Name}
                              </span>
                            ) : (
                              <span className={`text-xs font-medium italic ${
                                darkMode ? 'text-slate-500' : 'text-gray-400'
                              }`}>
                                Unassigned
                              </span>
                            )}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                            darkMode ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            {item.reorderStatus?.netAvailable ? (
                              <div className="flex flex-col gap-0.5">
                                <span className={`font-bold ${
                                  item.reorderStatus.netAvailable.base > 0
                                    ? (darkMode ? 'text-emerald-400' : 'text-emerald-700')
                                    : (darkMode ? 'text-red-400' : 'text-red-600')
                                }`}>
                                  {item.reorderStatus.netAvailable.base} {item.reorderStatus.netAvailable.unit || item.baseUnits}
                                </span>
                                {item.reorderStatus.netAvailable.additional > 0 && (
                                  <span className={`text-xs ${
                                    darkMode ? 'text-blue-400' : 'text-blue-600'
                                  }`}>
                                    +{item.reorderStatus.netAvailable.additional} {item.additionalUnits || 'PCS'}
                                  </span>
                                )}
                              </div>
                            ) : item.closingBalance ? (
                              <span className={`font-bold ${
                                parseFloat(item.closingBalance) > 0
                                  ? (darkMode ? 'text-emerald-400' : 'text-emerald-700')
                                  : (darkMode ? 'text-red-400' : 'text-red-600')
                              }`}>
                                {item.closingBalance} {item.baseUnits}
                              </span>
                            ) : (
                              <span className={`text-xs font-medium italic ${
                                darkMode ? 'text-slate-500' : 'text-gray-400'
                              }`}>
                                N/A
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(item.imageUrl || (item.imageIds && item.imageIds.length > 0)) ? (
                              <img
                                src={(() => {
                                  const firstImageId = item.imageIds[0];
                                  const imageId = typeof firstImageId === 'object' ? firstImageId._id : firstImageId;
                                  return item.imageUrl || `${process.env.REACT_APP_API_BASE_URL}/tally/images/${imageId}`;
                                })()}
                                alt={item.Name}
                                className={`w-12 h-12 object-cover rounded-lg border-2 cursor-pointer transition-all hover:scale-110 shadow-md ${
                                  darkMode
                                    ? 'border-slate-600 hover:border-blue-500'
                                    : 'border-gray-300 hover:border-purple-400'
                                }`}
                                onClick={() => {
                                  const firstImageId = item.imageIds[0];
                                  const imageId = typeof firstImageId === 'object' ? firstImageId._id : firstImageId;
                                  setShowImageModal(item.imageUrl || `${process.env.REACT_APP_API_BASE_URL}/tally/images/${imageId}`);
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className={`w-12 h-12 rounded-lg border-2 border-dashed hidden items-center justify-center ${
                              darkMode 
                                ? 'border-slate-600 bg-slate-800 text-slate-500' 
                                : 'border-gray-300 bg-gray-100 text-gray-400'
                            }`}>
                              <FaImage className="text-lg" />
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewItem(item)}
                                className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all shadow-md hover:shadow-lg"
                                title="View details"
                              >
                                <FaEye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEditItem(item)}
                                className={`p-2 rounded-lg transition-all shadow-md hover:shadow-lg ${
                                  darkMode
                                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                    : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                }`}
                                title="Edit item"
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleShowQR(item)}
                                className="p-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white transition-all shadow-md hover:shadow-lg"
                                title="Show QR Code"
                              >
                                <FaQrcode className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Pagination */
          <div className={`px-3 sm:px-6 py-4 border-t ${
            darkMode ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-gray-50'
          }`}>
            {/* Mobile: Stacked Layout */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Items per page - Mobile: Full width, Desktop: Left aligned */}
              <div className="flex items-center justify-center sm:justify-start gap-2 sm:gap-4">
                <span className={`text-xs sm:text-sm font-medium ${
                  darkMode ? 'text-slate-300' : 'text-gray-600'
                }`}>Per page:</span>
                <Select
                  value={{ value: limit, label: limit.toString() }}
                  onChange={(selectedOption) => {
                    setLimit(Number(selectedOption.value));
                    setPage(1);
                  }}
                  options={[
                    { value: 10, label: '10' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' },
                    { value: 200, label: '200' },
                    { value: 500, label: '500' },
                    { value: 1000, label: '1000' },
                    { value: 2000, label: '2000' }
                  ]}
                  isSearchable={false}
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '32px',
                      fontSize: '14px',
                      backgroundColor: darkMode ? 'rgb(51 65 85)' : 'white',
                      borderColor: darkMode ? 'rgb(71 85 105)' : '#d1d5db',
                      borderWidth: '2px'
                    }),
                    container: (base) => ({
                      ...base,
                      width: '80px'
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: darkMode ? 'rgb(51 65 85)' : 'white',
                      border: darkMode ? '2px solid rgb(71 85 105)' : '2px solid #d1d5db',
                      borderRadius: '0.5rem',
                      zIndex: 9999
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isFocused 
                        ? (darkMode ? 'rgb(71 85 105)' : 'rgb(243 232 255)')
                        : (darkMode ? 'rgb(51 65 85)' : 'white'),
                      color: darkMode ? 'rgb(229 231 235)' : 'rgb(17 24 39)',
                      fontWeight: '600',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: darkMode ? 'rgb(71 85 105)' : 'rgb(243 232 255)'
                      }
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: darkMode ? 'rgb(229 231 235)' : 'rgb(17 24 39)',
                      fontWeight: '600'
                    }),
                    indicatorSeparator: () => ({
                      display: 'none'
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: darkMode ? 'rgb(148 163 184)' : 'rgb(107 114 128)',
                      padding: '4px'
                    })
                  }}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              {/* Navigation Controls */}
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base font-medium transition-colors ${
                    page === 1 
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                      : 'bg-indigo-500 text-white hover:bg-indigo-600'
                  }`}
                >
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">‹</span>
                </button>
                
                <span className={`text-xs sm:text-sm px-2 sm:px-4 py-1 ${
                  darkMode ? 'text-slate-300' : 'text-gray-600'
                }`}>
                  <span className="hidden sm:inline">Page {page} of {Math.ceil(total / limit)}</span>
                  <span className="sm:hidden">{page}/{Math.ceil(total / limit)}</span>
                </span>
                
                <button
                  onClick={() => setPage(p => Math.min(Math.ceil(total / limit), p + 1))}
                  disabled={page >= Math.ceil(total / limit)}
                  className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base font-medium transition-colors ${
                    page >= Math.ceil(total / limit)
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-indigo-500 text-white hover:bg-indigo-600'
                  }`}
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">›</span>
                </button>
              </div>
            </div>
          </div>
}
        </div>

        </div> {/* Close w-full px-4 wrapper */}

        {/* Image Modal */}
        {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4" onClick={() => setShowImageModal(false)}>
          <div className="relative w-full h-full max-w-4xl max-h-full flex items-center justify-center">
            <img
              src={showImageModal}
              alt="Item"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-2 right-2 bg-white text-gray-800 p-2 sm:p-3 rounded-full hover:bg-gray-100 shadow-lg z-10"
            >
              <FaTimes className="text-base sm:text-lg" />
            </button>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {/* Premium Item Details Modal */}
      {showDetailsModal && selectedItem && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          <div className={`rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto my-auto transform transition-all border border-2 ${
            darkMode
              ? 'bg-gradient-to-br from-slate-900 to-slate-800 border-blue-500/30 shadow-blue-500/20'
              : 'bg-white border-purple-200/50 shadow-purple-500/20'
          }`} style={{ animation: 'slideUp 0.4s ease-out' }}>
            {/* Premium Header - Sticky */}
            <div className={`sticky top-0 z-10 px-3 sm:px-6 lg:px-8 py-3 sm:py-4 lg:py-5 border-b border-b-2 flex justify-between items-center backdrop-blur-xl ${
              darkMode
                ? 'bg-slate-900/95 border-slate-700/50'
                : 'bg-white/95 border-purple-200'
            }`}>
              <h3 className={`text-lg sm:text-xl lg:text-2xl font-black flex items-center gap-2 sm:gap-3 min-w-0 flex-1 ${
                darkMode
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400'
                  : 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600'
              }`}>
                <div className={`p-1.5 sm:p-2 lg:p-2.5 rounded-lg sm:rounded-xl shadow-lg flex-shrink-0 ${
                  darkMode
                    ? 'bg-gradient-to-br from-blue-600 to-purple-600'
                    : 'bg-gradient-to-br from-indigo-500 to-purple-500'
                }`}>
                  <FaEye className="text-white text-sm sm:text-base lg:text-lg" />
                </div>
                <span className="truncate text-sm sm:text-base lg:text-xl">{selectedItem.Name}</span>
              </h3>
              <button
                onClick={closeAllModals}
                className={`p-2 sm:p-2.5 rounded-lg sm:rounded-xl transition-all duration-300 transform hover:scale-110 hover:rotate-90 active:scale-95 flex-shrink-0 ml-2 ${
                  darkMode
                    ? 'text-red-400 hover:bg-red-900/30 shadow-lg'
                    : 'text-red-600 hover:bg-red-100 shadow-md'
                }`}
              >
                <FaTimes size={18} className="sm:hidden" />
                <FaTimes size={22} className="hidden sm:block" />
              </button>
            </div>
            
            <div className="p-3 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
              {/* Basic Information */}
              <div className={`border-b pb-4 ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                <h4 className={`text-base sm:text-lg font-semibold mb-3 ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>Basic Information</h4>
                <div className="grid grid-cols-1 gap-3 sm:gap-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>ID:</span>
                    <div className={`font-medium px-2 py-1 rounded text-xs sm:text-sm self-start ${
                      darkMode 
                        ? 'text-indigo-300 bg-indigo-900/50' 
                        : 'text-indigo-600 bg-indigo-50'
                    }`}>{selectedItem.id}</div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>GUID:</span>
                    <div className={`font-mono text-xs px-2 py-1 rounded break-all sm:max-w-xs ${
                      darkMode 
                        ? 'text-slate-300 bg-slate-700' 
                        : 'text-gray-600 bg-gray-50'
                    }`}>{selectedItem.GUID}</div>
                  </div>
                  <div>
                    <span className="text-xs sm:text-sm text-gray-500 font-medium">Name:</span>
                    <div className={`font-medium mt-1 p-2 rounded ${darkMode ? 'text-slate-200 bg-slate-700' : 'text-gray-900 bg-gray-50'}`}>{selectedItem.Name}</div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Category:</span>
                      <div className={`font-medium px-2 py-1 rounded text-xs sm:text-sm self-start ${
                        darkMode 
                          ? 'bg-blue-900/50 text-blue-300' 
                          : 'bg-blue-50 text-blue-800'
                      }`}>{selectedItem.category}</div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Parent:</span>
                      <div className={`font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>{selectedItem.parent || 'None'}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tax Information */}
              <div className={`border-b pb-4 ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                <h4 className={`text-base sm:text-lg font-semibold mb-3 ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>Tax Information</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>HSN Code:</span>
                    <div className={`font-medium px-2 py-1 rounded text-xs sm:text-sm self-start ${
                      darkMode 
                        ? 'bg-yellow-900/50 text-yellow-300' 
                        : 'bg-yellow-50 text-yellow-800'
                    }`}>{selectedItem.hsnCode}</div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>GST Applicable:</span>
                    <div className={`font-medium px-2 py-1 rounded text-xs sm:text-sm self-start ${
                      selectedItem.gstApplicable === 'Yes' 
                        ? (darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-50 text-green-800')
                        : (darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-50 text-red-800')
                    }`}>
                      {selectedItem.gstApplicable}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Integrated Tax:</span>
                    <div className={`font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>{selectedItem.integratedTax}%</div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Central Tax:</span>
                    <div className={`font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>{selectedItem.centralTax}%</div>
                  </div>
                </div>
              </div>

              {/* Units Information */}
              <div className={`border-b pb-4 ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                <h4 className={`text-base sm:text-lg font-semibold mb-3 ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>Units Information</h4>
                <div className="grid grid-cols-1 gap-3 sm:gap-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Base Units:</span>
                    <div className={`font-medium px-2 py-1 rounded text-xs sm:text-sm self-start ${
                      darkMode 
                        ? 'bg-purple-900/50 text-purple-300' 
                        : 'bg-purple-50 text-purple-800'
                    }`}>{selectedItem.baseUnits}</div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Additional Units:</span>
                    <div className={`font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>{selectedItem.additionalUnits || 'None'}</div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Closing Balance:</span>
                    <div className={`font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>{selectedItem.closingBalance}</div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Net Available:</span>
                    {selectedItem.reorderStatus?.netAvailable ? (
                      <div className="flex items-center gap-2">
                        <span className={`font-bold px-2 py-1 rounded text-xs sm:text-sm self-start ${
                          selectedItem.reorderStatus.netAvailable.base > 0
                            ? (darkMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-50 text-emerald-700')
                            : (darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-50 text-red-600')
                        }`}>
                          {selectedItem.reorderStatus.netAvailable.base} {selectedItem.reorderStatus.netAvailable.unit || selectedItem.baseUnits}
                        </span>
                        {selectedItem.reorderStatus.netAvailable.additional > 0 && (
                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                            darkMode ? 'text-blue-300 bg-blue-900/50' : 'text-blue-600 bg-blue-50'
                          }`}>
                            +{selectedItem.reorderStatus.netAvailable.additional} {selectedItem.additionalUnits || 'PCS'}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className={`font-medium italic text-xs sm:text-sm ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>N/A</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Product Information */}
              <div className={`border-b pb-4 ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                <h4 className={`text-base sm:text-lg font-semibold mb-3 ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>Product Information</h4>
                <div className="grid grid-cols-1 gap-3 sm:gap-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Part Number:</span>
                    <div className={`font-medium font-mono text-xs sm:text-sm ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>{selectedItem.partNo || 'Not specified'}</div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Alter ID:</span>
                    <div className={`font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>{selectedItem.alterID}</div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Section:</span>
                    <div className={`font-medium ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>{selectedItem.section || 'Not assigned'}</div>
                  </div>
                  <div>
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Remote Alt GUID:</span>
                    <div className={`font-mono text-xs px-2 py-1 rounded mt-1 break-all ${
                      darkMode 
                        ? 'text-slate-300 bg-slate-700' 
                        : 'text-gray-600 bg-gray-50'
                    }`}>
                      {selectedItem.remoteAltGUID}
                    </div>
                  </div>
                </div>
              </div>

              {/* Assignments */}
              <div className={`border-b pb-4 ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                <h4 className={`text-base sm:text-lg font-semibold mb-3 ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>Assignments</h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Parameter:</span>
                    {selectedItem.parameter ? (
                      <div className={`mt-1 p-3 rounded border ${
                        darkMode 
                          ? 'bg-green-900/30 border-green-700/50' 
                          : 'bg-green-50 border-green-200'
                      }`}>
                        <div className={`font-medium ${darkMode ? 'text-green-300' : 'text-green-800'}`}>{selectedItem.parameter.name}</div>
                        <div className={`text-xs ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                          Type: {selectedItem.parameter.calculationType === 'pcsPerBox' ? 'Pieces per Box' : 'Simple Pieces'}
                        </div>
                        {selectedItem.parameter.calculationType === 'pcsPerBox' && (
                          <div className={`text-xs mt-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                            Size: {selectedItem.parameter.width}×{selectedItem.parameter.height} | 
                            Box: {selectedItem.parameter.piecesPerBox} pcs | 
                            Coverage: {selectedItem.parameter.coverageArea} {selectedItem.parameter.coverageAreaUnit}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={`mt-1 p-3 rounded border text-sm ${
                        darkMode 
                          ? 'bg-slate-700/50 border-slate-600 text-slate-400' 
                          : 'bg-gray-50 border-gray-200 text-gray-500'
                      }`}>
                        No parameter assigned
                      </div>
                    )}
                  </div>
                  <div>
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Brand:</span>
                    {selectedItem.brand ? (
                      <div className={`mt-1 p-3 rounded border ${
                        darkMode 
                          ? 'bg-blue-900/30 border-blue-700/50' 
                          : 'bg-blue-50 border-blue-200'
                      }`}>
                        <div className={`font-medium ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>{selectedItem.brand.name}</div>
                      </div>
                    ) : (
                      <div className={`mt-1 p-3 rounded border text-sm ${
                        darkMode 
                          ? 'bg-slate-700/50 border-slate-600 text-slate-400' 
                          : 'bg-gray-50 border-gray-200 text-gray-500'
                      }`}>
                        No brand assigned
                      </div>
                    )}
                  </div>
                  <div>
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Series:</span>
                    <div className={`mt-1 p-3 rounded border ${
                      darkMode 
                        ? 'bg-purple-900/30 border-purple-700/50' 
                        : 'bg-purple-50 border-purple-200'
                    }`}>
                      <div className={`font-medium ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>
                        {selectedItem.series || 'RS'} - {seriesOptions.find(opt => opt.value === (selectedItem.series || 'RS'))?.label || 'Regular Stock (RS)'}
                      </div>
                    </div>
                  </div>
                  <div>
                    <span className={`text-xs sm:text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Assigned Category:</span>
                    {selectedItem.assigncategory ? (
                      <div className={`mt-1 p-3 rounded border ${
                        darkMode 
                          ? 'bg-orange-900/30 border-orange-700/50' 
                          : 'bg-orange-50 border-orange-200'
                      }`}>
                        <div className={`font-medium ${darkMode ? 'text-orange-300' : 'text-orange-800'}`}>{selectedItem.assigncategory.name}</div>
                      </div>
                    ) : (
                      <div className={`mt-1 p-3 rounded border text-sm ${
                        darkMode 
                          ? 'bg-slate-700/50 border-slate-600 text-slate-400' 
                          : 'bg-gray-50 border-gray-200 text-gray-500'
                      }`}>
                        No assigned category
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Stock Information */}
              {selectedItem.stock && selectedItem.stock.length > 0 && (
                <div className={`border-b pb-4 ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                  <h4 className={`text-base sm:text-lg font-semibold mb-3 ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>Stock Information</h4>
                  <div className="space-y-2">
                    {selectedItem.stock.map((stockItem, index) => (
                      <div key={index} className={`p-3 rounded border ${
                        darkMode 
                          ? 'bg-slate-700/50 border-slate-600' 
                          : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Location:</span>
                            <div className={`font-medium ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>{stockItem.location || 'Main Warehouse'}</div>
                          </div>
                          <div>
                            <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Quantity:</span>
                            <div className={`font-medium ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>{stockItem.quantity || 0} {selectedItem.baseUnits}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stock Summary by Location */}
              {selectedItem.stockSummary && selectedItem.stockSummary.length > 0 ? (
                <div className={`border-b pb-4 ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                  <h4 className={`text-base sm:text-lg font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                    <svg className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                    </svg>
                    Stock Summary by Location
                  </h4>
                  <div className="space-y-3">
                    {selectedItem.stockSummary.map((stockEntry, index) => (
                      <div key={index} className={`p-4 rounded-lg border ${
                        darkMode 
                          ? 'bg-gradient-to-r from-slate-800/50 to-slate-700/50 border-slate-600' 
                          : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
                      }`}>
                        <div className="mb-3">
                          <h5 className={`font-semibold flex items-center gap-2 ${
                            darkMode ? 'text-blue-300' : 'text-blue-800'
                          }`}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            </svg>
                            {stockEntry.location?.Name || stockEntry.location?.name || 'Unknown Location'}
                          </h5>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <div className={`p-3 rounded border ${
                              darkMode 
                                ? 'bg-slate-800/50 border-blue-600/50' 
                                : 'bg-white border-blue-300'
                            }`}>
                              <span className={`text-xs font-medium block mb-1 ${
                                darkMode ? 'text-slate-300' : 'text-gray-600'
                              }`}>Base Stock</span>
                              <div className="flex items-center justify-between">
                                <span className={`font-bold text-lg ${
                                  darkMode ? 'text-blue-300' : 'text-blue-700'
                                }`}>
                                  {stockEntry.stocks?.base?.quantity || 0}
                                </span>
                                <span className={`text-sm px-2 py-1 rounded ${
                                  darkMode 
                                    ? 'text-slate-300 bg-slate-700' 
                                    : 'text-gray-600 bg-gray-100'
                                }`}>
                                  {stockEntry.stocks?.base?.unit || selectedItem.baseUnits || 'PCS'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className={`p-3 rounded border ${
                              darkMode 
                                ? 'bg-slate-800/50 border-indigo-600/50' 
                                : 'bg-white border-indigo-300'
                            }`}>
                              <span className={`text-xs font-medium block mb-1 ${
                                darkMode ? 'text-slate-300' : 'text-gray-600'
                              }`}>Additional Stock</span>
                              <div className="flex items-center justify-between">
                                <span className={`font-bold text-lg ${
                                  darkMode ? 'text-indigo-300' : 'text-indigo-700'
                                }`}>
                                  {stockEntry.stocks?.additional?.quantity || 0}
                                </span>
                                <span className={`text-sm px-2 py-1 rounded ${
                                  darkMode 
                                    ? 'text-slate-300 bg-slate-700' 
                                    : 'text-gray-600 bg-gray-100'
                                }`}>
                                  {stockEntry.stocks?.additional?.unit || selectedItem.additionalUnits || 'PCS'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={`border-b pb-4 ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                  <h4 className={`text-base sm:text-lg font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>
                    <svg className={`w-5 h-5 ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                    </svg>
                    Stock Summary by Location
                  </h4>
                  <div className={`rounded-lg border p-4 ${
                    darkMode 
                      ? 'bg-yellow-900/20 border-yellow-700/50' 
                      : 'bg-yellow-50 border-yellow-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      <svg className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        darkMode ? 'text-yellow-400' : 'text-yellow-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                      </svg>
                      <div className="flex-1">
                        <h5 className={`font-medium mb-2 ${
                          darkMode ? 'text-yellow-300' : 'text-yellow-800'
                        }`}>Stock Summary Not Available</h5>
                        <p className={`text-sm mb-3 ${
                          darkMode ? 'text-yellow-200' : 'text-yellow-700'
                        }`}>
                          Location-wise stock data has not been synced for this item yet.
                        </p>
                        <div className={`p-3 rounded border ${
                          darkMode 
                            ? 'bg-yellow-900/30 border-yellow-700/50' 
                            : 'bg-yellow-100 border-yellow-300'
                        }`}>
                          <h6 className={`font-medium mb-2 ${
                            darkMode ? 'text-yellow-300' : 'text-yellow-800'
                          }`}>Possible reasons:</h6>
                          <ul className={`text-xs space-y-1 ${
                            darkMode ? 'text-yellow-200' : 'text-yellow-700'
                          }`}>
                            <li>• Stock Summary sync has not been run yet</li>
                            <li>• Tally server may not be responding to stock summary requests</li>
                            <li>• Item name mismatch between Tally and database</li>
                            <li>• No stock movement recorded for this item in Tally</li>
                          </ul>
                        </div>
                        <div className={`mt-3 p-3 rounded border ${
                          darkMode 
                            ? 'bg-blue-900/30 border-blue-700/50' 
                            : 'bg-blue-50 border-blue-200'
                        }`}>
                          <h6 className={`font-medium mb-2 ${
                            darkMode ? 'text-blue-300' : 'text-blue-800'
                          }`}>To enable stock summary:</h6>
                          <ol className={`text-xs space-y-1 ${
                            darkMode ? 'text-blue-200' : 'text-blue-700'
                          }`}>
                            <li>1. Ensure Tally is running and accessible</li>
                            <li>2. Go to Sync Management in admin panel</li>
                            <li>3. Run "Stock Summary" sync for this company</li>
                            <li>4. Wait for sync completion and refresh this page</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Price List Information */}
              {selectedItem.pricelist && selectedItem.pricelist.length > 0 && (
                <div className="border-b border-gray-200 pb-4">
                  <h4 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Price List
                  </h4>
                  <div className="space-y-3">
                    {selectedItem.pricelist.map((priceEntry, index) => (
                      <div key={index} className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {priceEntry.date && (
                            <div>
                              <span className="text-xs text-gray-500 font-medium">Date:</span>
                              <div className="font-medium text-gray-700">
                                {((d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`)(new Date(priceEntry.date)) || priceEntry.date}
                              </div>
                            </div>
                          )}
                          {priceEntry.priceLevel && (
                            <div>
                              <span className="text-xs text-gray-500 font-medium">Price Level:</span>
                              <div className="font-medium text-blue-700 bg-blue-100 px-2 py-1 rounded text-sm">
                                {priceEntry.priceLevel}
                              </div>
                            </div>
                          )}
                          {priceEntry.rates && priceEntry.rates.length > 0 && (
                            <div>
                              <span className="text-xs text-gray-500 font-medium">Rates:</span>
                              <div className="space-y-1">
                                {priceEntry.rates.map((rateItem, rateIndex) => (
                                  <div key={rateIndex} className="font-bold text-green-700 bg-green-100 px-2 py-1 rounded text-sm">
                                    <div className="text-green-800">₹{Math.ceil((rateItem.price || 0) + ((rateItem.price || 0) * 0.18)).toLocaleString('en-IN')}{rateItem.unit ? `/${rateItem.unit}` : ''} </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Items Information */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-lg font-semibold text-gray-700 mb-3">Related Information</h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Parent Group:</span>
                    <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded">
                      <div className="font-medium text-gray-700">{selectedItem.parent || 'No parent group'}</div>
                    </div>
                  </div>
                  {selectedItem.parentId && (
                    <div>
                      <span className="text-sm text-gray-500">Parent Group ID:</span>
                      <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded">
                        <div className="font-mono text-xs text-gray-600">{selectedItem.parentId.Name}</div>
                      </div>
                    </div>
                  )}
                  {selectedItem.size && (
                    <div>
                      <span className="text-sm text-gray-500">Size Reference:</span>
                      <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded">
                        <div className="font-mono text-xs text-gray-600">{selectedItem.size}</div>
                      </div>
                    </div>
                  )}
                  {(selectedItem.baseUnitId || selectedItem.additionalUnitId) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedItem.baseUnitId && (
                        <div>
                          <span className="text-sm text-gray-500">Base Unit ID:</span>
                          <div className="mt-1 p-2 bg-purple-50 border border-purple-200 rounded">
                            <div className="font-mono text-xs text-purple-700">{selectedItem.baseUnitId}</div>
                          </div>
                        </div>
                      )}
                      {selectedItem.additionalUnitId && (
                        <div>
                          <span className="text-sm text-gray-500">Additional Unit ID:</span>
                          <div className="mt-1 p-2 bg-purple-50 border border-purple-200 rounded">
                            <div className="font-mono text-xs text-purple-700">{selectedItem.additionalUnitId}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* QR Code Section */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-lg font-semibold text-gray-700 mb-3">QR Code</h4>
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-white border border-gray-200 rounded">
                    <QRCode value={selectedItem._id} size={80} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-2">
                      QR Code contains the item's unique ID for quick scanning and identification.
                    </p>
                    <button
                      onClick={() => handleShowQR(selectedItem)}
                      className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                    >
                      <FaQrcode />
                      View Full QR Code
                    </button>
                  </div>
                </div>
              </div>

              {/* Image Gallery Section */}
              {(selectedItem.imageUrl || (selectedItem.imageIds && selectedItem.imageIds.length > 0)) && (
                <div className={`pb-4 ${darkMode ? 'border-slate-700' : 'border-gray-200'} border-b`}>
                  <h4 className={`text-lg font-semibold mb-4 flex items-center gap-3 ${
                    darkMode ? 'text-slate-200' : 'text-gray-700'
                  }`}>
                    <div className={`p-2 rounded-xl shadow-lg ${
                      darkMode
                        ? 'bg-gradient-to-br from-emerald-600 to-blue-600'
                        : 'bg-gradient-to-br from-emerald-500 to-blue-500'
                    }`}>
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                    Product Images
                    {selectedItem.imageIds && selectedItem.imageIds.length > 0 && (
                      <span className={`text-sm px-3 py-1 rounded-full ${
                        darkMode 
                          ? 'bg-blue-900/50 text-blue-300 border border-blue-700' 
                          : 'bg-blue-100 text-blue-700 border border-blue-200'
                      }`}>
                        {selectedItem.imageIds.length} image{selectedItem.imageIds.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </h4>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {/* Legacy imageUrl support */}
                    {selectedItem.imageUrl && (
                      <div className="group relative">
                        <div className={`relative overflow-hidden rounded-xl border-2 transition-all duration-300 hover:shadow-xl ${
                          darkMode
                            ? 'border-slate-600 hover:border-blue-500 bg-slate-800'
                            : 'border-gray-200 hover:border-blue-400 bg-white'
                        }`}>
                          <img
                            src={selectedItem.imageUrl}
                            alt={selectedItem.Name}
                            className="w-full h-32 object-cover cursor-pointer transition-transform duration-300 group-hover:scale-110"
                            onClick={() => setShowImageModal(selectedItem.imageUrl)}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-2">
                            <span className="text-white text-xs font-medium">Click to expand</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* New imageIds support */}
                    {selectedItem.imageIds && selectedItem.imageIds.map((imageId, index) => (
                      <div key={imageId._id || imageId} className="group relative">
                        <div className={`relative overflow-hidden rounded-xl border-2 transition-all duration-300 hover:shadow-xl ${
                          darkMode
                            ? 'border-slate-600 hover:border-blue-500 bg-slate-800'
                            : 'border-gray-200 hover:border-blue-400 bg-white'
                        }`}>
                          <img
                            src={`${process.env.REACT_APP_API_BASE_URL}/tally/images/${imageId._id || imageId}`}
                            alt={`${selectedItem.Name} - Image ${index + 1}`}
                            className="w-full h-32 object-cover cursor-pointer transition-transform duration-300 group-hover:scale-110"
                            onClick={() => setShowImageModal(`${process.env.REACT_APP_API_BASE_URL}/tally/images/${imageId._id || imageId}`)}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className={`absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-2`}>
                            <span className="text-white text-xs font-medium">Click to expand</span>
                          </div>
                          {/* Error placeholder */}
                          <div className={`absolute inset-0 items-center justify-center hidden ${
                            darkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'
                          }`}>
                            <div className="text-center p-4">
                              <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                              </svg>
                              <span className="text-xs">Image not available</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* No images message */}
                  {!selectedItem.imageUrl && (!selectedItem.imageIds || selectedItem.imageIds.length === 0) && (
                    <div className={`text-center py-8 rounded-xl border-2 border-dashed ${
                      darkMode 
                        ? 'border-slate-600 bg-slate-800/50 text-slate-400' 
                        : 'border-gray-300 bg-gray-50 text-gray-500'
                    }`}>
                      <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <p className="text-sm font-medium">No images available</p>
                      <p className="text-xs mt-1">Images can be added through the edit modal</p>
                    </div>
                  )}
                </div>
              )}

              {/* Status Information */}
              <div>
                <h4 className="text-lg font-semibold text-gray-700 mb-3">Status & Metadata</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Assignment Status:</span>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedItem.isAssigned 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {selectedItem.isAssigned ? 'Fully Assigned' : 'Partially Assigned'}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Item ID:</span>
                    <div className="text-sm font-mono bg-indigo-50 text-indigo-700 px-2 py-1 rounded">{selectedItem.id}</div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Created:</span>
                    <div className="text-sm text-gray-600">
                      {((d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`)(new Date(selectedItem.createdAt))}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">Last Updated:</span>
                    <div className="text-sm text-gray-600">
                      {((d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`)(new Date(selectedItem.updatedAt))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {/* Premium Edit Item Modal */}
      {showEditModal && selectedItem && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          <div className={`rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto my-auto transform transition-all border-2 ${
            darkMode
              ? 'bg-gradient-to-br from-slate-900 to-slate-800 border-blue-500/30 shadow-blue-500/20'
              : 'bg-white border-purple-200/50 shadow-purple-500/20'
          }`} style={{ animation: 'slideUp 0.4s ease-out' }}>
            {/* Premium Header - Sticky */}
            <div className={`sticky top-0 z-10 px-8 py-5 border-b-2 flex justify-between items-center backdrop-blur-xl ${
              darkMode
                ? 'bg-slate-900/95 border-slate-700/50'
                : 'bg-white/95 border-purple-200'
            }`}>
              <h3 className={`text-2xl font-black flex items-center gap-3 ${
                darkMode
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400'
                  : 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600'
              }`}>
                <div className={`p-2.5 rounded-xl shadow-lg ${
                  darkMode
                    ? 'bg-gradient-to-br from-emerald-600 to-green-600'
                    : 'bg-gradient-to-br from-indigo-500 to-purple-500'
                }`}>
                  <FaEdit className="text-white text-lg" />
                </div>
                <span className="truncate max-w-md">{selectedItem.Name}</span>
              </h3>
              <button
                onClick={closeAllModals}
                className={`p-2.5 rounded-xl transition-all duration-300 transform hover:scale-110 hover:rotate-90 active:scale-95 ${
                  darkMode
                    ? 'text-red-400 hover:bg-red-900/30 shadow-lg'
                    : 'text-red-600 hover:bg-red-100 shadow-md'
                }`}
              >
                <FaTimes size={22} />
              </button>
            </div>
            
            <form onSubmit={handleItemUpdate} className="p-8 space-y-6">
              {/* Item Name - Read Only */}
              <div>
                <label className={`block text-sm font-black uppercase tracking-wider mb-3 ${
                  darkMode ? 'text-blue-400' : 'text-purple-700'
                }`}>
                  📦 Item Name
                </label>
                <input
                  type="text"
                  value={itemFormData.Name}
                  className={`w-full p-4 rounded-xl border-2 font-bold transition-all ${
                    darkMode
                      ? 'bg-slate-800/50 border-slate-600 text-slate-400 cursor-not-allowed'
                      : 'bg-gray-50 border-gray-300 text-gray-600 cursor-not-allowed'
                  }`}
                  disabled
                />
                <p className={`text-xs font-medium mt-2 ${
                  darkMode ? 'text-slate-500' : 'text-gray-500'
                }`}>
                  ℹ️ Item name cannot be changed (synced from Tally)
                </p>
              </div>

              {/* Parameter Selection */}
              <div>
                <label className={`block text-sm font-black uppercase tracking-wider mb-3 ${
                  darkMode ? 'text-blue-400' : 'text-purple-700'
                }`}>
                  <FaTags className="inline mr-2" />
                  Parameter {parameters.length > 0 && <span className="text-xs opacity-70">({parameters.length} available)</span>}
                </label>
                <Select
                  options={parameterOptions}
                  value={parameterOptions.find(opt => opt.value === itemFormData.parameter)}
                  onChange={(option) => setItemFormData(prev => ({ ...prev, parameter: option?.value || '' }))}
                  placeholder={
                    loading ? "Loading parameters..." : 
                    parameters.length === 0 ? "No parameters available" : 
                    "Select parameter"
                  }
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                  isClearable
                  isDisabled={loading || parameters.length === 0}
                  isLoading={loading}
                  noOptionsMessage={() => 
                    parameters.length === 0 ? 
                    "No parameters found. Create parameters in Tally Parameters Management." : 
                    "No matching parameters found"
                  }
                />
                {parameters.length === 0 && !loading && (
                  <p className={`mt-2 text-xs font-bold flex items-center gap-2 ${
                    darkMode ? 'text-yellow-400' : 'text-amber-600'
                  }`}>
                    <FaExclamationTriangle />
                    No parameters available. Please create parameters first in Tally Parameters Management.
                  </p>
                )}
              </div>

              {/* Brand Selection */}
              <div>
                <label className={`block text-sm font-black uppercase tracking-wider mb-3 ${
                  darkMode ? 'text-blue-400' : 'text-purple-700'
                }`}>
                  <FaTags className="inline mr-2" />
                  Brand
                </label>
                <Select
                  options={brandOptions}
                  value={brandOptions.find(opt => opt.value === itemFormData.brand)}
                  onChange={(option) => setItemFormData(prev => ({ ...prev, brand: option?.value || '' }))}
                  placeholder="Select brand"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                  isClearable
                />
              </div>

              {/* Series Selection */}
              <div>
                <label className={`block text-sm font-black uppercase tracking-wider mb-3 ${
                  darkMode ? 'text-blue-400' : 'text-purple-700'
                }`}>
                  <FaTags className="inline mr-2" />
                  Series
                </label>
                <Select
                  options={seriesOptions}
                  value={seriesOptions.find(opt => opt.value === itemFormData.series)}
                  onChange={(option) => setItemFormData(prev => ({ ...prev, series: option?.value || 'RS' }))}
                  placeholder="Select series"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                  isClearable
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className={`block text-sm font-black uppercase tracking-wider mb-3 ${
                  darkMode ? 'text-blue-400' : 'text-purple-700'
                }`}>
                  <FaCamera className="inline mr-2" />
                  Item Images
                </label>
                
                {/* Existing Images */}
                {itemFormData.existingImages && itemFormData.existingImages.length > 0 && (
                  <div className="mb-4">
                    <h4 className={`text-sm font-semibold mb-2 ${
                      darkMode ? 'text-slate-300' : 'text-gray-700'
                    }`}>
                      Current Images:
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {itemFormData.existingImages.map((imageUrl, index) => (
                        <div key={`existing-${index}`} className="relative group">
                          <img
                            src={imageUrl}
                            alt={`Existing ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg shadow-md"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveExistingImage(index)}
                            className={`absolute -top-2 -right-2 p-1.5 rounded-full shadow-lg transition-all transform hover:scale-110 group-hover:opacity-100 opacity-75 ${
                              darkMode
                                ? 'bg-red-600 hover:bg-red-500 text-white'
                                : 'bg-red-500 hover:bg-red-600 text-white'
                            }`}
                          >
                            <FaTimes size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Images Preview */}
                {itemFormData.imagePreviews && itemFormData.imagePreviews.length > 0 && (
                  <div className="mb-4">
                    <h4 className={`text-sm font-semibold mb-2 ${
                      darkMode ? 'text-slate-300' : 'text-gray-700'
                    }`}>
                      New Images:
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {itemFormData.imagePreviews.map((preview, index) => (
                        <div key={`new-${index}`} className="relative group">
                          <img
                            src={preview}
                            alt={`New ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg shadow-md"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className={`absolute -top-2 -right-2 p-1.5 rounded-full shadow-lg transition-all transform hover:scale-110 group-hover:opacity-100 opacity-75 ${
                              darkMode
                                ? 'bg-red-600 hover:bg-red-500 text-white'
                                : 'bg-red-500 hover:bg-red-600 text-white'
                            }`}
                          >
                            <FaTimes size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload New Images */}
                <div className={`border-2 border-dashed rounded-2xl p-6 transition-all ${
                  darkMode
                    ? 'border-slate-600 bg-slate-800/30 hover:border-blue-500'
                    : 'border-gray-300 bg-gray-50 hover:border-purple-400'
                }`}>
                  <div className="text-center py-4">
                    <FaImage className={`mx-auto h-12 w-12 mb-3 ${
                      darkMode ? 'text-slate-600' : 'text-gray-400'
                    }`} />
                    <div className="mt-2">
                      <label htmlFor="image-upload-modal" className="cursor-pointer">
                        <span className={`font-bold text-lg ${
                          darkMode
                            ? 'text-blue-400 hover:text-blue-300'
                            : 'text-indigo-600 hover:text-indigo-500'
                        }`}>Add more images</span>
                        <p className={`text-sm mt-2 ${
                          darkMode ? 'text-slate-500' : 'text-gray-500'
                        }`}>PNG, JPG, GIF up to 10MB (Multiple selection allowed)</p>
                        <input
                          id="image-upload-modal"
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          multiple
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 py-4 rounded-xl font-black shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${
                    darkMode
                      ? 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white shadow-emerald-500/50'
                      : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-indigo-500/30'
                  }`}
                >
                  {loading ? '⏳ Updating...' : '✓ Update Item'}
                </button>
                <button
                  type="button"
                  onClick={closeAllModals}
                  className={`px-8 py-4 rounded-xl font-black transition-all duration-300 hover:scale-105 active:scale-95 ${
                    darkMode
                      ? 'bg-slate-700 hover:bg-slate-600 text-white border-2 border-slate-600'
                      : 'bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-300'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <FaQrcode className="text-purple-600" />
                QR Code
              </h3>
              <button
                onClick={closeAllModals}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FaTimes size={20} />
              </button>
            </div>
            <div className="p-6 text-center space-y-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200 inline-block">
                <QRCode 
                  id="qr-code-svg"
                  value={qrCodeData} 
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#000000"
                />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">{selectedItem.Name}</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Scan this QR code to quickly access item details
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => downloadQR(false)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <FaDownload />
                    Download QR Only
                  </button>
                  <button
                    onClick={() => downloadQR(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaDownload />
                    Download with QR Name
                  </button>
                  {!qrName && (
                    <p className="text-xs text-gray-500 text-center mt-1">
                      Using default name "DOMUS" (QR Name not configured)
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock Summary Modal */}
      {showStockSummaryModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <FaWarehouse className="text-green-600" />
                Stock Summary - {selectedItem.Name}
              </h3>
              <button
                onClick={closeAllModals}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {selectedItem.stockSummary && selectedItem.stockSummary.length > 0 ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-600">Total Locations</p>
                          <p className="text-2xl font-bold text-blue-900">
                            {selectedItem.stockSummary.length}
                          </p>
                        </div>
                        <FaWarehouse className="h-8 w-8 text-blue-500" />
                      </div>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-600">Total Base Stock</p>
                          <p className="text-2xl font-bold text-green-900">
                            {selectedItem.stockSummary.reduce((total, stock) => 
                              total + (stock.stocks?.base?.quantity || 0), 0
                            ).toFixed(2)}
                          </p>
                        </div>
                        <FaBox className="h-8 w-8 text-green-500" />
                      </div>
                    </div>
                    
                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-orange-600">Total Value</p>
                          <p className="text-2xl font-bold text-orange-900">
                            ₹{selectedItem.stockSummary.reduce((total, stock) => 
                              total + (stock.closingValue || 0), 0
                            ).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <FaChartBar className="h-8 w-8 text-orange-500" />
                      </div>
                    </div>
                  </div>

                  {/* Stock Details Table */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-800">Location-wise Stock Details</h4>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Location
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Base Stock
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Additional Stock
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Standard Cost
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Closing Value
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedItem.stockSummary.map((stock, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <FaWarehouse className="h-4 w-4 text-gray-400 mr-2" />
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {stock.location?.Name || 'Unknown Location'}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {stock.location?.alterID && `ID: ${stock.location.alterID}`}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  <span className="font-medium">
                                    {(stock.stocks?.base?.quantity || 0).toLocaleString('en-IN', { 
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2 
                                    })}
                                  </span>
                                  <span className="text-gray-500 ml-1">
                                    {stock.stocks?.base?.unit || 'PCS'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  <span className="font-medium">
                                    {(stock.stocks?.additional?.quantity || 0).toLocaleString('en-IN', { 
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2 
                                    })}
                                  </span>
                                  <span className="text-gray-500 ml-1">
                                    {stock.stocks?.additional?.unit || 'PCS'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  ₹{(stock.standardCost || 0).toLocaleString('en-IN', { 
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2 
                                  })}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-green-600">
                                  ₹{(stock.closingValue || 0).toLocaleString('en-IN', { 
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2 
                                  })}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Additional Item Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h5 className="text-md font-semibold text-gray-800 mb-3">Additional Information</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-600">Item ID:</span>
                        <span className="ml-2 text-gray-900">{selectedItem.id}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">GUID:</span>
                        <span className="ml-2 text-gray-900 font-mono text-xs">{selectedItem.GUID}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Base Units:</span>
                        <span className="ml-2 text-gray-900">{selectedItem.baseUnits}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Additional Units:</span>
                        <span className="ml-2 text-gray-900">{selectedItem.additionalUnits}</span>
                      </div>
                      {selectedItem.hsnCode && (
                        <div>
                          <span className="font-medium text-gray-600">HSN Code:</span>
                          <span className="ml-2 text-gray-900">{selectedItem.hsnCode}</span>
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-gray-600">Last Updated:</span>
                        <span className="ml-2 text-gray-900">
                          {selectedItem.updatedAt ? 
                            ((d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`)(new Date(selectedItem.updatedAt)) : 'N/A'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FaExclamationTriangle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No Stock Summary Available</h4>
                  <p className="text-gray-600 mb-4">
                    Stock summary data has not been synced for this item yet.
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-left max-w-md mx-auto">
                    <h5 className="font-medium text-blue-800 mb-2">To view stock summary:</h5>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Ensure Tally is connected and active</li>
                      <li>• Run a "Stock Summary" sync from the sync management</li>
                      <li>• Wait for the sync to complete</li>
                      <li>• Refresh this page to see updated data</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Premium QR Scanner Modal */}
      {showQRScanner && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          <div className={`rounded-3xl shadow-2xl w-full max-w-lg max-h-[95vh] overflow-y-auto my-auto transform transition-all ${
            darkMode
              ? 'bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-blue-500/30 shadow-blue-500/20'
              : 'bg-gradient-to-br from-white to-purple-50 border-2 border-purple-200/50 shadow-purple-500/20'
          }`} style={{ animation: 'slideUp 0.4s ease-out' }}>
            {/* Premium Header */}
            <div className={`px-6 py-4 border-b-2 flex justify-between items-center ${
              darkMode
                ? 'border-slate-700/50 bg-gradient-to-r from-blue-900/20 to-purple-900/20'
                : 'border-purple-200/50 bg-gradient-to-r from-blue-50 to-purple-50'
            }`}>
              <h3 className={`text-xl font-black flex items-center gap-2 ${
                darkMode
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400'
                  : 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600'
              }`}>
                <div className={`p-2 rounded-xl shadow-lg transform hover:scale-110 transition-all ${
                  darkMode
                    ? 'bg-gradient-to-br from-blue-600 to-purple-600 shadow-blue-500/50'
                    : 'bg-gradient-to-br from-indigo-500 to-purple-500 shadow-purple-500/30'
                }`}>
                  <FaCamera className="text-white text-lg" />
                </div>
                <span style={{ animation: 'float 3s ease-in-out infinite' }}>Scan QR Code</span>
              </h3>
              <button
                onClick={stopQRScanner}
                className={`p-2 rounded-xl transition-all duration-300 transform hover:scale-110 hover:rotate-90 active:scale-95 ${
                  darkMode
                    ? 'text-red-400 hover:bg-red-900/30 hover:shadow-red-500/50 shadow-lg'
                    : 'text-red-600 hover:bg-red-100 hover:shadow-red-500/30 shadow-md'
                }`}
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            {/* Camera Selection */}
            <div className={`px-6 py-3 border-b-2 ${
              darkMode
                ? 'border-slate-700/50 bg-slate-800/50'
                : 'border-purple-100 bg-purple-50/30'
            }`}>
              <label className={`block text-xs font-black uppercase tracking-wider mb-2 ${
                darkMode ? 'text-blue-400' : 'text-purple-700'
              }`}>
                📹 Select Camera:
              </label>
              <select
                value={selectedCameraId}
                onChange={(e) => {
                  const newCamera = e.target.value;
                  setSelectedCameraId(newCamera);
                  // Update facing mode based on camera label if available
                  const selectedCamera = availableCameras.find(cam => cam.id === newCamera);
                  if (selectedCamera) {
                    const label = selectedCamera.label.toLowerCase();
                    setCameraFacingMode(
                      label.includes('back') || label.includes('rear') ? 'environment' : 'user'
                    );
                  }
                }}
                className={`w-full px-3 py-2 text-sm font-bold rounded-xl border-2 transition-all focus:outline-none focus:ring-4 shadow-md ${
                  darkMode
                    ? 'bg-slate-700 border-slate-600 text-white focus:ring-blue-500/30 focus:border-blue-500 hover:border-blue-600'
                    : 'bg-white border-purple-300 text-gray-900 focus:ring-purple-500/30 focus:border-purple-500 hover:border-purple-400'
                }`}
              >
                {availableCameras.map((camera) => (
                  <option key={camera.id} value={camera.id}>
                    {camera.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="p-6">
              {/* Scanner Frame */}
              <div className={`relative rounded-2xl overflow-hidden shadow-2xl border-4 w-full h-[380px] ${
                darkMode
                  ? 'border-blue-500/50 shadow-blue-500/30 bg-black'
                  : 'border-purple-300/50 shadow-purple-500/20 bg-black'
              }`}>
                <div className="w-full h-full">
                  <QRScanner
                    onScan={handleQRScan}
                    cameraId={selectedCameraId}
                    onCameraError={handleQRError}
                    onLoading={handleCameraLoad}
                  />
                </div>
                
                {/* Scanner Overlay Frame */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-0 border-4 border-transparent" style={{
                    background: 'linear-gradient(to right, transparent 40%, rgba(147, 51, 234, 0.1) 50%, transparent 60%)',
                    animation: 'scanLine 2s linear infinite'
                  }}></div>
                  
                  {/* Corner Markers */}
                  <div className={`absolute top-3 left-3 w-10 h-10 border-t-4 border-l-4 rounded-tl-xl ${
                    darkMode ? 'border-blue-500' : 'border-purple-500'
                  }`}></div>
                  <div className={`absolute top-3 right-3 w-10 h-10 border-t-4 border-r-4 rounded-tr-xl ${
                    darkMode ? 'border-blue-500' : 'border-purple-500'
                  }`}></div>
                  <div className={`absolute bottom-3 left-3 w-10 h-10 border-b-4 border-l-4 rounded-bl-xl ${
                    darkMode ? 'border-blue-500' : 'border-purple-500'
                  }`}></div>
                  <div className={`absolute bottom-3 right-3 w-10 h-10 border-b-4 border-r-4 rounded-br-xl ${
                    darkMode ? 'border-blue-500' : 'border-purple-500'
                  }`}></div>
                </div>
                
                {qrScannerLoading && (
                  <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center">
                    <div className={`animate-spin rounded-full h-12 w-12 border-b-4 mb-3 ${
                      darkMode ? 'border-blue-500' : 'border-purple-500'
                    }`}></div>
                    <p className="text-white text-center font-bold text-base">
                      Initializing Camera...
                    </p>
                  </div>
                )}
              </div>
              
              {/* Instructions */}
              <div className={`mt-4 p-3 rounded-2xl border-2 text-center ${
                darkMode
                  ? 'bg-slate-800/50 border-slate-700 text-slate-300'
                  : 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200 text-gray-700'
              }`}>
                <p className="text-xs font-bold">
                  📱 Position the QR code within the frame to scan automatically
                </p>
              </div>
              
              {/* Permission Notice */}
              <div className={`mt-3 p-3 rounded-2xl border-2 shadow-lg ${
                darkMode
                  ? 'bg-blue-900/30 border-blue-700/50 text-blue-300'
                  : 'bg-blue-50 border-blue-200 text-blue-700'
              }`}>
                <p className="text-xs font-bold text-center flex items-center justify-center gap-2">
                  <span className="text-base">💡</span>
                  <span>Make sure to allow camera permissions when prompted by your browser</span>
                </p>
              </div>
              
              {/* Action Buttons */}
              <div className="mt-4 flex justify-center gap-3">
                <button
                  onClick={stopQRScanner}
                  className={`px-6 py-2.5 rounded-xl font-black shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 text-sm ${
                    darkMode
                      ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white shadow-red-500/50'
                      : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white shadow-red-500/30'
                  }`}
                >
                  🛑 Stop Scanning
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scanLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
      `}</style>


      {/* Bulk Assignment Modal */}
      {showBulkAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Bulk Assign to {selectedItems.size} Items
                </h3>
                <button
                  onClick={() => setShowBulkAssignModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Parameter Assignment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign Parameter
                  </label>
                  <Select
                    value={bulkParameterOptions.find(option => option.value === bulkAssignData.parameter)}
                    onChange={(selectedOption) => setBulkAssignData(prev => ({ 
                      ...prev, 
                      parameter: selectedOption ? selectedOption.value : '' 
                    }))}
                    options={bulkParameterOptions}
                    placeholder="Don't change parameter"
                    isSearchable
                    isClearable
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '42px',
                        borderColor: '#d1d5db'
                      })
                    }}
                  />
                </div>

                {/* Brand Assignment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign Brand
                  </label>
                  <Select
                    value={bulkBrandOptions.find(option => option.value === bulkAssignData.brand)}
                    onChange={(selectedOption) => setBulkAssignData(prev => ({ 
                      ...prev, 
                      brand: selectedOption ? selectedOption.value : '' 
                    }))}
                    options={bulkBrandOptions}
                    placeholder="Don't change brand"
                    isSearchable
                    isClearable
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '42px',
                        borderColor: '#d1d5db'
                      })
                    }}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowBulkAssignModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkAssign}
                  disabled={loading || (!bulkAssignData.parameter && !bulkAssignData.brand)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Assigning...' : 'Apply Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      </div> {/* Close main container */}
    </>
  );
}
